import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { saveVioExternalApiKey } from '../services/api';

export default function VioExternalApiKeyModal({
  opened,
  allowDismiss,
  statusError,
  onClose,
  onSaved,
  onRetryStatus,
}) {
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showKey, setShowKey] = useState(false);

  if (!opened) return null;

  const handleConfirm = async () => {
    const trimmed = apiKey.trim();
    if (!trimmed) {
      setError('Paste your VIO External API key to continue.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await saveVioExternalApiKey(trimmed);
      if (!res.success) {
        setError(res.message || 'Save failed');
        return;
      }
      setApiKey('');
      onSaved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (allowDismiss && e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div className="modal-overlay vio-key-modal-overlay" onClick={handleOverlayClick}>
      <div className="modal vio-key-modal">
        <div className="modal-header">
          <h3>VIO External API Key</h3>
          {allowDismiss && (
            <button type="button" className="modal-close" onClick={onClose}>&times;</button>
          )}
        </div>

        <div className="modal-body vio-key-modal-body">
          {(statusError || error) && (
            <div className="vio-key-alert vio-key-alert--error">
              {statusError || error}
            </div>
          )}

          <div className="vio-key-instructions">
            <p className="vio-key-instructions-title">
              Configure your VIO External API key so this portal can call VIO
              (tokens, vouchers, External API v1).
            </p>
            <ol className="vio-key-steps">
              <li>
                In VIO, open <strong>Settings</strong> and go to the{' '}
                <strong>API Keys</strong> section (External API).
              </li>
              <li>
                Create a new API key with the permissions required for External
                API access (see VIO documentation).
              </li>
              <li>Copy the key and paste it into the field below.</li>
              <li>
                Click <strong>Confirm</strong>. The key is stored securely for
                your tenant on the backend (not in the browser).
              </li>
            </ol>
          </div>

          <label className="vio-key-label">External API key</label>
          <div className="vio-key-input-wrapper">
            <input
              type={showKey ? 'text' : 'password'}
              className="vio-key-input"
              placeholder="Paste your vio_live_… key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              autoComplete="off"
            />
            <button
              type="button"
              className="vio-key-toggle-vis"
              onClick={() => setShowKey((v) => !v)}
              aria-label={showKey ? 'Hide key' : 'Show key'}
            >
              {showKey ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>

          <div className="vio-key-actions">
            <button
              type="button"
              className="btn btn-primary vio-key-confirm-btn"
              onClick={handleConfirm}
              disabled={saving || !apiKey.trim()}
            >
              {saving ? 'Saving…' : 'Confirm'}
            </button>
            {statusError && onRetryStatus && (
              <button
                type="button"
                className="btn btn-secondary vio-key-retry-btn"
                onClick={onRetryStatus}
              >
                Retry status check
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
